import { Bell, CheckCheck } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useActionsNotification, useCompteurNotifications, useNotifications } from '@/api/hooks';
import type { Notification } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn, dateHeureFr } from '@/lib/utils';

/** Une notification de la liste : titre, message, date ; ouverture du document lié et marquage comme lue. */
export function LigneNotification({ n, onOuvrir }: { n: Notification; onOuvrir: (n: Notification) => void }) {
  return (
    <li>
      <button type="button" onClick={() => onOuvrir(n)} className={cn('w-full rounded-md px-3 py-2 text-left hover:bg-muted', !n.lue && 'bg-primary/5')}>
        <div className="flex items-start justify-between gap-2">
          <span className={cn('text-sm', !n.lue && 'font-semibold')}>
            {n.priorite === 'URGENTE' && <span className="mr-1 inline-block size-2 rounded-full bg-red-600 align-middle" aria-label="Urgent" />}
            {n.titre}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">{dateHeureFr(n.createdAt)}</span>
        </div>
        <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.message}</div>
      </button>
    </li>
  );
}

/** Cloche de l'en-tête : compteur des non lues, tiroir des dernières notifications, accès à la page complète. */
export function ClocheNotifications() {
  const navigate = useNavigate();
  const [ouvert, setOuvert] = useState(false);
  const compteur = useCompteurNotifications();
  const liste = useNotifications(false);
  const actions = useActionsNotification();
  const nonLues = compteur.data?.nonLues ?? 0;

  const ouvrir = (n: Notification) => {
    if (!n.lue) actions.lue.mutate(n.id);
    setOuvert(false);
    if (n.lien) navigate(n.lien);
  };

  return (
    <Sheet open={ouvert} onOpenChange={setOuvert}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={nonLues ? `${nonLues} notification(s) non lue(s)` : 'Notifications'}>
          <Bell className="size-5" />
          {nonLues > 0 && <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-red-600 px-1 text-center text-[10px] font-semibold leading-4 text-white">{nonLues > 99 ? '99+' : nonLues}</span>}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <SheetTitle>Notifications</SheetTitle>
          <Button variant="ghost" size="sm" onClick={() => actions.toutLu.mutate()} disabled={nonLues === 0 || actions.toutLu.isPending}>
            <CheckCheck className="size-4" /> Tout marquer lu
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto px-1 py-2">
          {liste.data?.length ? (
            <ul className="space-y-0.5">
              {liste.data.slice(0, 30).map((n) => (
                <LigneNotification key={n.id} n={n} onOuvrir={ouvrir} />
              ))}
            </ul>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Aucune notification.</div>
          )}
        </div>
        <div className="border-t px-4 py-3 text-sm">
          <Link to="/notifications" className="text-primary hover:underline" onClick={() => setOuvert(false)}>
            Toutes les notifications et préférences
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
